import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../supabase";
import { C, MOIS, mono } from "../config/theme";
import { fmt, fmtK } from "../utils/format";
import KpiCard from "../components/KpiCard";
import FilterBar from "../components/FilterBar";
import MiniTable from "../components/MiniTable";
import DrillDown from "../components/DrillDown";
import StatusMessage from "../components/StatusMessage";

/* ─── Couleurs statut chiffrage ─── */
const STATUT_CHIFFRAGE = {
  en_cours:    { bg: "#EFF6FF", color: C.blue,    border: C.blue,    label: "En cours" },
  "envoyé":    { bg: "#FFFBEB", color: C.accent,  border: C.accent,  label: "Envoyé" },
  envoye:      { bg: "#FFFBEB", color: C.accent,  border: C.accent,  label: "Envoyé" },
  "accepté":   { bg: C.greenBg,  color: C.green,   border: C.green,   label: "Accepté" },
  accepte:     { bg: C.greenBg,  color: C.green,   border: C.green,   label: "Accepté" },
  "refusé":    { bg: C.redBg,    color: C.red,     border: C.red,     label: "Refusé" },
  refuse:      { bg: C.redBg,    color: C.red,     border: C.red,     label: "Refusé" },
  sans_suite:  { bg: C.bg,       color: C.slate,   border: C.border,  label: "Sans suite" },
};

function StatutChiffrageBadge({ statut }) {
  const v = STATUT_CHIFFRAGE[statut] || { bg: C.bg, color: C.slate, border: C.border, label: statut };
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      background: v.bg, color: v.color, border: `1px solid ${v.border}40`, whiteSpace: "nowrap",
    }}>
      {v.label}
    </span>
  );
}

function SectionTitle({ children, color = C.navy }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
      color, margin: "24px 0 12px", paddingBottom: 8, borderBottom: `2px solid ${color}20`,
    }}>
      {children}
    </div>
  );
}

/* ─── Helper : taux ratio (0.28) → pourcentage formaté ─── */
const tauxFmt = (n) => n == null ? "—" : `${(Number(n) * 100).toFixed(1)}%`;

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE COMMERCIAL — Dashboard chargé d'affaires
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function PageCommercial() {
  /* ─── Filtres ─── */
  const [annee, setAnnee] = useState(null);
  const [filChargeAffaires, setFilChargeAffaires] = useState("");

  /* ─── Référentiels (annees, charges_affaires) — réutilise bv_rpc_ca_filtres ─── */
  const [filtresData, setFiltresData] = useState(null);

  /* ─── Données RPCs ─── */
  const [global, setGlobal] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [marches, setMarches] = useState(null);

  const [loadingFiltres, setLoadingFiltres] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);

  /* ─── Paramètres RPC ─── */
  const rpcParams = useMemo(() => ({
    p_annee: annee,
    ...(filChargeAffaires ? { p_charge_affaires_id: filChargeAffaires } : {}),
  }), [annee, filChargeAffaires]);

  /* ─── Init : filtres ─── */
  useEffect(() => {
    async function loadFiltres() {
      setLoadingFiltres(true);
      const { data, error: err } = await supabase.rpc("bv_rpc_ca_filtres");
      if (err) {
        console.error("Erreur filtres commercial:", err);
        setError("Impossible de charger les filtres.");
        setLoadingFiltres(false);
        return;
      }
      setFiltresData(data);
      if (data?.annees?.length > 0) setAnnee(data.annees[0]);
      setLoadingFiltres(false);
    }
    loadFiltres();
  }, []);

  /* ─── Chargement données ─── */
  useEffect(() => {
    if (!annee) return;

    async function loadData() {
      setLoadingData(true);
      try {
        const [r1, r2, r3] = await Promise.all([
          supabase.rpc("bv_rpc_commercial_global", rpcParams),
          supabase.rpc("bv_rpc_commercial_pipeline", rpcParams),
          supabase.rpc("bv_rpc_commercial_marches", rpcParams),
        ]);
        if (r1.data) setGlobal(r1.data);
        if (r2.data) setPipeline(r2.data);
        if (r3.data) setMarches(r3.data);
        [r1, r2, r3].forEach((r, i) => {
          if (r.error) console.error(`Erreur RPC commercial #${i + 1}:`, r.error);
        });
      } catch (err) {
        console.error("Erreur chargement commercial:", err);
        setError("Erreur lors du chargement des données.");
      } finally {
        setLoadingData(false);
      }
    }
    loadData();
  }, [annee, rpcParams]);

  /* ─── Colonnes pipeline ─── */
  const colonnesPipeline = useMemo(() => [
    { key: "code_erp", label: "Code", bold: true, mono: true },
    { key: "numero_piece", label: "N° devis", mono: true },
    {
      key: "date_piece", label: "Date", mono: true,
      render: (r) => r.date_piece ? new Date(r.date_piece).toLocaleDateString("fr-FR") : "—",
    },
    { key: "statut", label: "Statut", render: (r) => <StatutChiffrageBadge statut={r.statut} /> },
    { key: "libelle", label: "Libellé", bold: true },
    { key: "client", label: "Client" },
    { key: "charge_affaires", label: "Chargé d'affaires" },
    {
      key: "montant_ht", label: "Montant HT", align: "right", mono: true, bold: true,
      render: (r) => fmt(r.montant_ht),
    },
    {
      key: "taux_marge_brute", label: "Marge brute %", align: "right", mono: true,
      render: (r) => tauxFmt(r.taux_marge_brute),
      color: (r) => {
        const t = Number(r.taux_marge_brute || 0) * 100;
        return t < 15 ? C.red : t < 25 ? C.accent : C.green;
      },
    },
    {
      key: "taux_marge_nette", label: "Marge nette %", align: "right", mono: true,
      render: (r) => tauxFmt(r.taux_marge_nette),
      color: (r) => {
        const t = Number(r.taux_marge_nette || 0) * 100;
        return t < 10 ? C.red : t < 18 ? C.accent : C.green;
      },
    },
  ], []);

  /* ─── Transformation par mois — sort + compléter labels ─── */
  const transfoRows = useMemo(() => {
    const rows = (pipeline?.transformation_par_mois || []).slice().sort((a, b) => a.mois - b.mois);
    return rows.map((r) => ({ ...r, mois_label: MOIS[r.mois] }));
  }, [pipeline]);

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDU
     ═══════════════════════════════════════════════════════════════════════════ */
  if (loadingFiltres) {
    return <div style={{ padding: "40px 0", textAlign: "center", color: C.slate }}>Chargement…</div>;
  }
  if (error) {
    return <StatusMessage type="error" message={error} action={{ label: "Réessayer", onClick: () => window.location.reload() }} />;
  }
  if (!filtresData?.annees?.length) {
    return <StatusMessage type="empty" message="Aucune donnée disponible." detail="Les données n'ont pas encore été synchronisées depuis l'ERP." />;
  }
  if (!annee) return null;

  return (
    <div>
      {/* ─── En-tête ─── */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>Commercial</h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: C.slate }}>
          Pipeline chiffrages, transformation, marges signées
        </p>
      </div>

      {/* ─── Filtres ─── */}
      <FilterBar
        annees={filtresData.annees} annee={annee} setAnnee={setAnnee}
        showPeriode={false}
        showClient={false}
        showConducteur={false}
        chargesAffaires={filtresData.charges_affaires || []}
        filChargeAffaires={filChargeAffaires} setFilChargeAffaires={setFilChargeAffaires}
        activites={[]}
        agences={[]}
      />

      {loadingData && (
        <div style={{ textAlign: "center", padding: "12px 0", color: C.slate, fontSize: 13, marginBottom: 8 }}>
          Actualisation des données…
        </div>
      )}

      {/* ═══ KPI LIGNE 1 — Volumétrie pipeline + portefeuille ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 12 }}>
        <KpiCard label="Chiffrages en cours" value={global?.nb_chiffrages_en_cours || 0} color={C.blue} isCurrency={false} />
        <KpiCard label="Montant portefeuille" value={global?.montant_portefeuille || 0} color={C.accent} />
        <KpiCard
          label="Taux transformation"
          value={global?.taux_transformation || 0}
          color={(global?.taux_transformation || 0) >= 50 ? C.green : C.accent}
          suffix="%"
        />
        <KpiCard label={`Marchés signés ${annee}`} value={global?.marches_signes_ytd || 0} color={C.green} />
      </div>

      {/* ═══ KPI LIGNE 2 — Marges + délai ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginBottom: 16 }}>
        <KpiCard
          label="Marge nette moyenne"
          value={global?.marge_nette_moyenne || 0}
          color={(global?.marge_nette_moyenne || 0) < 10 ? C.red : (global?.marge_nette_moyenne || 0) < 18 ? C.accent : C.green}
          suffix="%"
        />
        <KpiCard
          label="Devis en attente"
          value={global?.nb_devis_attente || 0}
          color={(global?.nb_devis_attente || 0) > 0 ? C.accent : C.slateLight}
          isCurrency={false}
        />
        <KpiCard
          label="Délai moyen de réponse"
          value={global?.delai_moyen_reponse || 0}
          color={C.slate}
          isCurrency={false}
          suffix=" j"
        />
      </div>

      {/* ═══ PIPELINE CHIFFRAGES ═══ */}
      <SectionTitle color={C.blue}>Pipeline chiffrages</SectionTitle>
      {(pipeline?.pipeline || []).length > 0 ? (
        <DrillDown
          title={`Chiffrages en cours / envoyés / acceptés (${pipeline.pipeline.length})`}
          color={C.blue}
          columns={colonnesPipeline}
          rows={pipeline.pipeline}
        />
      ) : (
        <StatusMessage type="empty" message="Aucun chiffrage actif sur cette période." />
      )}

      {/* ═══ CHIFFRAGES PERDUS (si présents) ═══ */}
      {(pipeline?.perdus || []).length > 0 && (
        <>
          <SectionTitle color={C.red}>Chiffrages perdus / sans suite</SectionTitle>
          <DrillDown
            title={`Chiffrages perdus (${pipeline.perdus.length})`}
            color={C.red}
            columns={colonnesPipeline}
            rows={pipeline.perdus}
          />
        </>
      )}

      {/* ═══ PORTEFEUILLE PAR CLIENT + TRANSFORMATION PAR MOIS ═══ */}
      <SectionTitle color={C.accent}>Portefeuille & transformation</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        <MiniTable
          title="Portefeuille par client"
          color={C.accent}
          headers={[
            { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
            { key: "client", label: "Client", bold: true, maxW: 220, total: () => "Total" },
            { key: "nb_chiffrages", label: "Chiffrages", align: "right", mono: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_chiffrages || 0), 0) },
            { key: "montant_ht", label: "Montant HT", align: "right", mono: true, bold: true,
              render: (r) => fmt(r.montant_ht),
              total: (rows) => fmt(rows.reduce((a, r) => a + (r.montant_ht || 0), 0)) },
          ]}
          rows={pipeline?.portefeuille_par_client || []}
        />

        <MiniTable
          title="Transformation par mois"
          color={C.green}
          headers={[
            { key: "mois_label", label: "Mois", bold: true, total: () => "Total" },
            { key: "nb_total", label: "Émis", align: "right", mono: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_total || 0), 0) },
            { key: "nb_acceptes", label: "Acceptés", align: "right", mono: true, bold: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_acceptes || 0), 0) },
            { key: "taux", label: "Taux", align: "right", mono: true,
              render: (r) => `${Number(r.taux || 0).toFixed(1)}%`,
              total: (rows) => {
                const total = rows.reduce((a, r) => a + (r.nb_total || 0), 0);
                const acc = rows.reduce((a, r) => a + (r.nb_acceptes || 0), 0);
                return total > 0 ? `${((acc / total) * 100).toFixed(1)}%` : "—";
              } },
          ]}
          rows={transfoRows}
        />
      </div>

      {/* ═══ MARCHÉS SIGNÉS RÉCENTS ═══ */}
      <SectionTitle color={C.green}>Marchés signés</SectionTitle>
      {(marches?.marches_recents || []).length > 0 ? (
        <MiniTable
          title={`Marchés signés récents (${marches.marches_recents.length})`}
          color={C.green}
          headers={[
            { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
            { key: "libelle", label: "Libellé", maxW: 240, total: () => "Total" },
            { key: "client", label: "Client", maxW: 180, total: () => "" },
            { key: "date_piece", label: "Date", mono: true,
              render: (r) => r.date_piece ? new Date(r.date_piece).toLocaleDateString("fr-FR") : "—",
              total: () => "" },
            { key: "montant_ht", label: "Montant HT", align: "right", mono: true, bold: true,
              render: (r) => fmt(r.montant_ht),
              total: (rows) => fmt(rows.reduce((a, r) => a + (r.montant_ht || 0), 0)) },
            { key: "taux_marge_brute", label: "Marge brute", align: "right", mono: true,
              render: (r) => tauxFmt(r.taux_marge_brute), total: () => "" },
            { key: "taux_marge_nette", label: "Marge nette", align: "right", mono: true,
              render: (r) => tauxFmt(r.taux_marge_nette), total: () => "" },
          ]}
          rows={marches.marches_recents}
        />
      ) : (
        <StatusMessage type="empty" message="Aucun marché signé sur cette période." />
      )}

      {/* ═══ MARCHÉ INITIAL VS TS ═══ */}
      {(marches?.marche_vs_ts || []).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <MiniTable
            title="Marché initial vs travaux supplémentaires"
            color="#8B5CF6"
            headers={[
              { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
              { key: "chantier", label: "Chantier", maxW: 320, total: () => "Total" },
              { key: "marche_initial", label: "Marché initial", align: "right", mono: true,
                render: (r) => fmt(r.marche_initial),
                total: (rows) => fmt(rows.reduce((a, r) => a + (r.marche_initial || 0), 0)) },
              { key: "ts_total", label: "TS", align: "right", mono: true,
                render: (r) => fmt(r.ts_total),
                color: (r) => (r.ts_total || 0) > 0 ? C.accent : C.slateLight,
                total: (rows) => fmt(rows.reduce((a, r) => a + (r.ts_total || 0), 0)) },
              { key: "marche_total", label: "Marché total", align: "right", mono: true, bold: true,
                render: (r) => fmt(r.marche_total),
                total: (rows) => fmt(rows.reduce((a, r) => a + (r.marche_total || 0), 0)) },
              { key: "pct_ts", label: "% TS", align: "right", mono: true,
                render: (r) => {
                  const init = r.marche_initial || 0;
                  if (init === 0) return "—";
                  return `${(((r.ts_total || 0) / init) * 100).toFixed(1)}%`;
                },
                total: () => "" },
            ]}
            rows={marches.marche_vs_ts}
          />
        </div>
      )}

      {/* ═══ MARGES CHIFFRAGE VS MARCHÉ ═══ */}
      <SectionTitle color="#F97316">Marges chiffrage vs marché signé</SectionTitle>
      {(marches?.marges_chiffrages || []).length > 0 ? (
        <MiniTable
          title="Comparaison marge prévue / marge réalisée à la signature"
          color="#F97316"
          headers={[
            { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
            { key: "libelle", label: "Libellé", maxW: 240, total: () => "Total" },
            { key: "statut", label: "Statut", render: (r) => <StatutChiffrageBadge statut={r.statut} />, total: () => "" },
            { key: "chiffrage_ht", label: "Chiffrage HT", align: "right", mono: true,
              render: (r) => fmtK(r.chiffrage_ht), total: () => "" },
            { key: "chiffrage_taux_marge", label: "Marge chiffrage", align: "right", mono: true,
              render: (r) => tauxFmt(r.chiffrage_taux_marge), total: () => "" },
            { key: "marche_ht", label: "Marché HT", align: "right", mono: true,
              render: (r) => r.marche_ht != null ? fmtK(r.marche_ht) : "—", total: () => "" },
            { key: "marche_taux_marge", label: "Marge marché", align: "right", mono: true,
              render: (r) => tauxFmt(r.marche_taux_marge), total: () => "" },
            {
              key: "ecart", label: "Écart marge", align: "right", mono: true, bold: true,
              render: (r) => {
                if (r.marche_taux_marge == null || r.chiffrage_taux_marge == null) return "—";
                const ecart = (r.marche_taux_marge - r.chiffrage_taux_marge) * 100;
                const isNeg = ecart < 0;
                return (
                  <span style={{ color: isNeg ? C.red : C.green }}>
                    {isNeg ? "" : "+"}{ecart.toFixed(2)} pts
                  </span>
                );
              },
              total: () => "",
            },
          ]}
          rows={marches.marges_chiffrages}
        />
      ) : (
        <StatusMessage type="empty" message="Aucun chiffrage à comparer." />
      )}

      {/* ─── Note méthodo ─── */}
      <div style={{ marginTop: 12, fontSize: 11, color: C.slateLight, fontStyle: "italic", fontFamily: mono }}>
        Marche HT « — » : chiffrage non encore transformé en marché signé. Écart positif = marge réalisée &gt; marge prévue.
      </div>
    </div>
  );
}
